import { tienePermiso } from "./autorizacion"
import type { UsuarioSesion } from "./sesion"

/**
 * Quién puede ABRIR cada pantalla del sistema por su dirección (05/11, pedido del
 * usuario).
 *
 * ⚠ **El caso que lo pidió**: con la sesión de alguien sin esos permisos, escribir
 * `/admin/usuarios` o `/tesoreria/metodos` montaba la pantalla —título, pestañas, la
 * tabla vacía y el error de cada consulta— aunque el menú no la ofreciera. El servidor
 * respondía 403 a cada petición, pero la página enseñaba su estructura: qué módulos
 * hay, qué pestañas y qué columnas. Ahora la pantalla ni se monta ni pide nada.
 *
 * ⚠ **Sigue siendo COSMÉTICO** (`lib/autorizacion.ts`): quien decide es el servidor en
 * cada petición. Esto evita enseñar lo que el servidor no va a servir.
 *
 * ⚠ **Cada regla es el permiso de su entrada del menú** (`cli.py::MODULOS`): lo que el
 * menú no ofrece, la dirección no lo abre. `accesoRutas.test.ts` falla si una entrada
 * del menú pide otro permiso que su regla, o si una ruta de `main.tsx` no tiene regla.
 * Las pestañas de un departamento siguen pidiendo el suyo dentro de su pantalla.
 */

export type Regla =
  /** Una dirección vieja que sólo redirige: la de destino tiene su propia regla. */
  | { redirige: true }
  | {
      /** Qué clase de cuenta la abre. */
      quien: "todos" | "interno" | "cliente"
      /** Basta con UNO. Sin permisos, la abre cualquiera de esa clase. */
      permisos?: readonly string[]
    }

const TODOS: Regla = { quien: "todos" }
const INTERNO: Regla = { quien: "interno" }
const REDIRIGE: Regla = { redirige: true }
const interno = (...permisos: string[]): Regla => ({ quien: "interno", permisos })
const cliente = (...permisos: string[]): Regla => ({ quien: "cliente", permisos })

/**
 * Por PREFIJO de ruta: `/expedientes` cubre `/expedientes/12`. Manda el más largo,
 * así `/portal/buzon` tiene la suya y `/portal/7` cae en `/portal`.
 */
export const REGLAS: Readonly<Record<string, Regla>> = {
  // Cualquiera con sesión.
  "/": TODOS,
  "/perfil": TODOS,
  // Lo de cada quien del personal: su ficha, sus avisos, sus solicitudes.
  "/avisos": INTERNO,
  "/mi-ficha": INTERNO,
  "/guia": INTERNO,
  "/mis-evaluaciones": INTERNO,
  "/mis-documentos": interno("documento_th:solicitar"),
  // Los módulos, con el permiso de su entrada del menú.
  "/tareas": interno("tarea:ver"),
  "/reportes": interno("reporte:triar"),
  "/buzon": interno("buzon:ver"),
  "/expedientes": interno("expediente:ver"),
  "/clientes": interno("cliente:ver"),
  "/cotizaciones": interno("cotizacion:ver"),
  "/proveedores": interno("proveedor:ver"),
  "/tipos-proveedor": interno("proveedor:ver"),
  "/administracion": interno("administracion:ver"),
  "/talento-humano": interno("talento_humano:ver"),
  "/tesoreria": interno("tesoreria:ver"),
  // Sus dos pestañas piden cada una lo suyo: con cualquiera de las dos hay qué ver.
  "/libro-cuentas": interno("cxp:ver", "cxc:ver"),
  "/admin/usuarios": interno("usuario:administrar"),
  "/admin/auditoria": interno("auditoria:ver"),
  "/planificacion": interno("planificacion:ver"),
  "/empresa": interno("empresa:administrar"),
  "/drive": interno("drive:ver"),
  "/credenciales": interno("credencial:ver"),
  "/libro-compras": interno("libro_compras:ver"),
  "/plan-cuentas": interno("gasto:ver"),
  "/resumen-macro": interno("resumen_macro:ver"),
  "/retenciones-provisionales": interno("retencion_provisional:ver"),
  "/pendientes": interno("pendiente:ver"),
  "/cadena-suministros": interno("cadena_suministro:ver"),
  "/cumplimiento": interno("cumplimiento:ver"),
  "/identidad": interno("identidad:ver"),
  "/rotulacion": interno("rotulo:ver"),
  "/plantillas": interno("plantilla:ver"),
  "/sistema-de-diseno": interno("usuario:administrar"),
  "/mapa": interno("mapa:ver"),
  "/fletes": interno("transporte:ver"),
  // El portal del importador: sólo su cuenta, con los permisos de su menú.
  "/portal": cliente("expediente:ver"),
  "/portal/estado-cuenta": cliente("cxc:ver"),
  "/portal/buzon": cliente("buzon:ver"),
  // Direcciones viejas: llevan a su sitio nuevo, que decide.
  "/nomina": REDIRIGE,
  "/personas": REDIRIGE,
  "/ordenes-pago": REDIRIGE,
  "/gastos": REDIRIGE,
  "/cxc": REDIRIGE,
  "/cxp": REDIRIGE,
  "/informes/gastos": REDIRIGE,
  "/comercial": REDIRIGE,
  "/catalogo": REDIRIGE,
  "/estrategia": REDIRIGE,
  "/organigrama": REDIRIGE,
  "/procesos": REDIRIGE,
  "/biblioteca": REDIRIGE,
  "/manual": REDIRIGE,
  "/carnets": REDIRIGE,
  "/inventario": REDIRIGE,
}

/** La regla de una ruta, por su prefijo más largo. `undefined`: ninguna pantalla. */
export function reglaDe(ruta: string): Regla | undefined {
  const limpia = ruta.length > 1 ? ruta.replace(/\/+$/, "") : ruta
  if (limpia === "/") return REGLAS["/"]
  let actual = limpia
  while (actual.length > 1) {
    const regla = REGLAS[actual]
    if (regla !== undefined) return regla
    actual = actual.slice(0, actual.lastIndexOf("/"))
  }
  return undefined
}

/**
 * Si esta persona puede abrir esa ruta.
 *
 * Una ruta sin regla es la de «Ruta no encontrada», que no enseña nada: se deja
 * pasar para que lo diga. Que ninguna pantalla real se quede sin regla lo vigila
 * `accesoRutas.test.ts`.
 */
export function puedeAbrirRuta(usuario: UsuarioSesion | null, ruta: string): boolean {
  const regla = reglaDe(ruta)
  if (regla === undefined || "redirige" in regla) return true
  if (usuario === null) return false
  if (regla.quien === "interno" && usuario.tipo !== "INTERNO") return false
  if (regla.quien === "cliente" && usuario.tipo !== "CLIENTE") return false
  if (regla.permisos === undefined) return true
  return regla.permisos.some((p) => tienePermiso(usuario, p))
}
